import { apiClient } from './apiClient';

// DRM Quota types
export interface DrmQuotaRemainingDto {
  remaining: number;
  quotaType: QuotaType;
  studentId: string;
  bookId: string;
}

export interface DrmQuotaDto {
  id: string;
  studentId: string;
  bookId: string;
  quotaType: QuotaType;
  limit: number;
  used: number;
  rollingWindowDays: number;
  resetDate: string;
  isOverridden: boolean;
}

export enum QuotaType {
  Print = 1,
  Copy = 2,
  Download = 3,
}

export interface PrintRequestDto {
  studentId: string;
  bookId: string;
  pageCount: number;
}

export interface CopyEventDto {
  studentId: string;
  bookId: string;
  copiedTextLength: number;
}

export interface DownloadRequestDto {
  studentId: string;
  bookId: string;
  chapterId?: string | null;
}

export interface DrmRequestResultDto {
  isApproved: boolean;
  message: string;
  remainingQuota: number;
}

export interface UpdateDrmQuotaDto {
  limit?: number | null;
  rollingWindowDays?: number | null;
  isOverridden?: boolean;
}

/**
 * Get remaining print quota for a student and book
 */
export const getPrintQuotaRemaining = async (
  studentId: string,
  bookId: string
): Promise<DrmQuotaRemainingDto> => {
  const response = await apiClient.get<DrmQuotaRemainingDto>(
    '/api/app/drm-quotas-extn/print-quota',
    { params: { studentId, bookId } }
  );
  return response.data;
};

/**
 * Get remaining copy quota for a student and book
 */
export const getCopyQuotaRemaining = async (
  studentId: string,
  bookId: string
): Promise<DrmQuotaRemainingDto> => {
  const response = await apiClient.get<DrmQuotaRemainingDto>(
    '/api/app/drm-quotas-extn/copy-quota',
    { params: { studentId, bookId } }
  );
  return response.data;
};

/**
 * Get remaining download quota for a student and book
 */
export const getDownloadQuotaRemaining = async (
  studentId: string,
  bookId: string
): Promise<DrmQuotaRemainingDto> => {
  const response = await apiClient.get<DrmQuotaRemainingDto>(
    '/api/app/drm-quotas-extn/download-quota',
    { params: { studentId, bookId } }
  );
  return response.data;
};

/**
 * Request permission to print pages
 */
export const requestPrint = async (
  request: PrintRequestDto
): Promise<DrmRequestResultDto> => {
  const response = await apiClient.post<DrmRequestResultDto>(
    '/api/app/drm-quotas-extn/print-request',
    request
  );
  return response.data;
};

/**
 * Log a copy event
 */
export const logCopyEvent = async (
  event: CopyEventDto
): Promise<DrmRequestResultDto> => {
  const response = await apiClient.post<DrmRequestResultDto>(
    '/api/app/drm-quotas-extn/copy-event',
    event
  );
  return response.data;
};

/**
 * Request permission to download
 */
export const requestDownload = async (
  request: DownloadRequestDto
): Promise<DrmRequestResultDto> => {
  const response = await apiClient.post<DrmRequestResultDto>(
    '/api/app/drm-quotas-extn/download-request',
    request
  );
  return response.data;
};

/**
 * Get all DRM quotas for a student (Print, Copy, Download)
 */
export const getStudentQuotasSummary = async (
  studentId: string,
  bookId: string
): Promise<{
  print: DrmQuotaRemainingDto;
  copy: DrmQuotaRemainingDto;
  download: DrmQuotaRemainingDto;
}> => {
  const [print, copy, download] = await Promise.all([
    getPrintQuotaRemaining(studentId, bookId),
    getCopyQuotaRemaining(studentId, bookId),
    getDownloadQuotaRemaining(studentId, bookId),
  ]);

  return { print, copy, download };
};

/**
 * Generate watermarked PDF for printing
 */
export interface GeneratePrintPdfDto {
  studentId: string;
  bookId: string;
  startPage: number;
  endPage: number;
}

export interface WatermarkedPdfResultDto {
  isApproved: boolean;
  downloadUrl?: string;
  message: string;
  remainingQuota: number;
  pageCount: number;
  requestId: string;
}

export const generatePrintPdf = async (
  request: GeneratePrintPdfDto
): Promise<WatermarkedPdfResultDto> => {
  const response = await apiClient.post<WatermarkedPdfResultDto>(
    '/api/app/drm-quotas-extn/generate-print-pdf',
    request
  );
  return response.data;
};

/**
 * Generate watermarked PDF for download
 * (Uses same endpoint as print with different intent)
 */
export const generateDownloadPdf = async (
  request: GeneratePrintPdfDto
): Promise<WatermarkedPdfResultDto> => {
  const response = await apiClient.post<WatermarkedPdfResultDto>(
    '/api/app/drm-quotas-extn/generate-print-pdf',
    request
  );
  return response.data;
};
